import { Box, Skeleton } from '@mui/material';
import React, { MutableRefObject, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useBundleTranslation from 'i18n';
import { useIsConfigConstantEqual } from 'hooks/useConfigConstant';
import { ExternalContentDisplayType } from 'components/element-viewer';
import { useConciergeContext } from 'hooks/useConciergeContextProvider';
import { ConciergeContextData } from 'app/concierge/conciergeContext';

function useAdjustIframeLayout(
    iframeRef: MutableRefObject<HTMLIFrameElement | undefined>,
    iframeLoaded: boolean,
    imageSize?: { height: number; width: number },
): [number, number] {
    const [resultHeight, setResultHeight] = useState(0);
    const [resultWidth, setResultWidth] = useState(0);
    const adjustIframeHeight = function () {
        if (!imageSize || imageSize?.width == 0 || !iframeRef.current) {
            setResultHeight(0);
            setResultWidth(0);
            return;
        }

        let height;
        const src = iframeRef.current.src;
        const iWidth = iframeRef.current.getBoundingClientRect().width;
        if ('undefined' !== typeof src && src && -1 !== src.indexOf('index/intermediate/')) {
            height = Math.floor((imageSize.height / imageSize.width) * iWidth) + 1;
        } else {
            height = Math.floor(((imageSize.height + 75) / imageSize.width) * iWidth) + 35;
        }

        let width = Math.min(Math.max(1128, imageSize.width * (1 + 75 / imageSize.height)), 32700);

        setResultHeight(height < 650 ? 650 : height);
        setResultWidth(width);
    };
    useEffect(() => {
        const onResize = () => {
            adjustIframeHeight();
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [imageSize, iframeRef]);

    useEffect(() => {
        adjustIframeHeight();
    }, [iframeRef.current, imageSize, iframeLoaded]);

    return [resultHeight, resultWidth];
}

export default function EmbedElement({
    embedUrl,
    displayType,
    url,
    iframeHeight,
    iframeWidth,
    fullScreen,
    imageSize,
    children,
    refreshFrequency,
}: {
    embedUrl: string;
    displayType: ExternalContentDisplayType;
    fullScreen?: boolean;
    url?: string;
    iframeHeight?: number;
    iframeWidth?: number;
    imageSize?: {
        height: number;
        width: number;
    };
    children?: ReactNode;
    refreshFrequency?: number;
}) {
    const { t } = useBundleTranslation(['components/report-content']);
    const [searchParams] = useSearchParams();
    const [isLoaded, setIsLoaded] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>();
    const handleOnLoad = (e: any) => {
        if (displayType == 'image') {
            if (imgRef?.current?.complete) {
                setIsLoaded(true);
            }
        } else {
            if (e?.target?.src != window.location.href && e?.target?.src != encodeURI(window.location.href)) {
                setIsLoaded(true);
            }
        }
    };

    useEffect(() => {
        setIsLoaded(false);
    }, [embedUrl]);

    const imgRef = useRef<HTMLImageElement>(null);
    const image = (
        <img
            ref={imgRef}
            onLoad={handleOnLoad}
            onError={handleOnLoad}
            style={{ minHeight: '650px', maxWidth: '100%', display: 'block' }}
            src={embedUrl}
        />
    );

    const [adjustHeight, adjustWidth] = useAdjustIframeLayout(
        iframeRef,
        isLoaded,
        displayType == 'iframe' ? imageSize : undefined,
    );

    const embedElementContext = useRef<ConciergeContextData | null>(null);
    const pendingContextRequests = useRef<Map<string, (value: ConciergeContextData | null) => void>>(new Map());

    useEffect(() => {
        if (!iframeRef.current) {
            return;
        }
        const handler = (event: MessageEvent) => {
            if (event.data?.command != 'context' || !event.data?.context) {
                return;
            }
            embedElementContext.current = event.data.context;
            const requestId: string = event.data?.requestId ?? '';
            if (requestId && pendingContextRequests.current.has(requestId)) {
                const resolve = pendingContextRequests.current.get(requestId)!;
                pendingContextRequests.current.delete(requestId);
                resolve(event.data.context);
            }
        };
        window.addEventListener('message', handler, false);
        return () => {
            window.removeEventListener('message', handler, false);
        };
    }, [iframeRef]);

    const conciergeContextFunc = useCallback(
        () =>
            new Promise<ConciergeContextData | null>((resolve) => {
                if (!iframeRef.current) {
                    resolve(embedElementContext.current);
                    return;
                }
                const requestId = Math.random().toString(36).slice(2) + Date.now().toString(36);
                pendingContextRequests.current.set(requestId, resolve);
                iframeRef.current.contentWindow?.postMessage({ command: 'getContext', requestId: requestId }, '*');
                setTimeout(() => {
                    if (pendingContextRequests.current.has(requestId)) {
                        pendingContextRequests.current.delete(requestId);
                        resolve(embedElementContext.current);
                    }
                }, 2000);
            }),
        [],
    );
    useConciergeContext('EmbedElement', conciergeContextFunc);

    //Default size
    let width = '100%';
    let height = fullScreen ? '100%' : '650px';

    //Full Height get param
    const fullHeight = searchParams.has('fullHeight');
    if (fullHeight) height = '100%';

    if (displayType == 'image') {
        //Image always full height
        height = '100%';
    } else if (displayType == 'iframe') {
        //External props of size
        if (!fullHeight && iframeHeight) {
            height = iframeHeight + 'px';
        }
        if (iframeWidth) {
            width = iframeWidth + 'px';
        }

        //Size by aspect ratio
        if (!fullHeight) {
            if (adjustHeight > 0) {
                height = adjustHeight + 'px';
            }
            if (adjustWidth > 0) {
                width = adjustWidth + 'px';
            }
        }
    }

    const isAIClipboard = useIsConfigConstantEqual('ALLOW_IFRAME_CLIPBOARD_READ_WRITE', true);

    const refreshInterval = useRef(0);
    useEffect(() => {
        if (!refreshFrequency || refreshFrequency <= 0 || !iframeRef.current) {
            return;
        }
        if (refreshInterval.current) {
            clearInterval(refreshInterval.current);
        }
        refreshInterval.current = window.setInterval(
            () => {
                if (iframeRef?.current?.src) {
                    // Will trigger iframe reload
                    iframeRef.current.src = iframeRef.current.src;
                }
            },
            refreshFrequency * 60 * 1000,
        );

        return () => {
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
                refreshInterval.current = 0;
            }
        };
    }, [iframeRef.current, refreshFrequency]);

    return (
        <Box
            className={'embed-element-holder'}
            data-test={'external-embed-element'}
            sx={{
                width: fullScreen ? '100%' : width,
                height: fullScreen ? '100%' : height,
                overflow: 'auto',
                textAlign: 'center',
                position: 'relative',
                flexShrink: 0,
                minWidth: '100%',
            }}
        >
            {!isLoaded && (
                <Skeleton
                    sx={{ height: '100%', width: '100%', position: 'absolute' }}
                    animation="wave"
                    variant="rectangular"
                />
            )}
            {displayType == 'image' ? (
                url ? (
                    <Box
                        component={'a'}
                        target={'_blank'}
                        rel="noopener noreferrer"
                        href={url}
                        sx={{ display: 'inline-block', maxWidth: '100%' }}
                    >
                        {image}
                    </Box>
                ) : (
                    <Box sx={{ display: 'inline-block', maxWidth: '100%' }}>{image}</Box>
                )
            ) : (
                <Box
                    className={'embed-element-iframe'}
                    allow={
                        isAIClipboard
                            ? 'clipboard-read; clipboard-write; local-network-access *'
                            : 'local-network-access *'
                    }
                    ref={iframeRef}
                    id={'iframe-component'}
                    onLoad={handleOnLoad}
                    onError={handleOnLoad}
                    component="iframe"
                    width={width}
                    height={height}
                    src={embedUrl}
                    sx={{ border: 0, display: 'block', minHeight: iframeHeight ? undefined : '650px' }}
                />
            )}
            {isLoaded && children}
        </Box>
    );
}
